
import React, { useState } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

const MarketingPage: React.FC = () => {
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    marketingType: 'email'
  });
  const [newsletterSubmitted, setNewsletterSubmitted] = useState(false);
  const [contactSubmitted, setContactSubmitted] = useState(false);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newsletterEmail.trim()) return;

    try {
      const formData = new FormData();
      formData.append('email', newsletterEmail);
      formData.append('subscription_type', 'newsletter');

      const response = await fetch('https://readdy.ai/api/form/d44h98mpguuav66rggkg', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        setNewsletterSubmitted(true);
        setNewsletterEmail('');
        setTimeout(() => setNewsletterSubmitted(false), 3000);
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
    }
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactForm.name.trim() || !contactForm.email.trim()) return;

    try {
      const formData = new FormData();
      Object.entries(contactForm).forEach(([key, value]) => {
        formData.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d44h98mpguuav66rggl0', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        setContactSubmitted(true);
        setContactForm({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: '',
          marketingType: 'email'
        });
        setTimeout(() => setContactSubmitted(false), 3000);
      }
    } catch (error) {
      console.error('Contact form error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-blue-50 to-indigo-100 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Stay Connected with LoanGuide Pro
              </h1>
              <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
                Get the latest mortgage guidelines, industry updates, and exclusive insights delivered directly to your inbox. Join thousands of mortgage professionals who trust our updates.
              </p>
            </div>
          </div>
        </section>

        {/* Newsletter Subscription */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 md:p-12 text-white">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold mb-4">Subscribe to Our Newsletter</h2>
                <p className="text-blue-100 text-lg">
                  Get weekly updates on new guidelines, regulatory changes, and industry insights
                </p>
              </div>
              
              <form onSubmit={handleNewsletterSubmit} className="max-w-md mx-auto" data-readdy-form>
                <div className="flex flex-col sm:flex-row gap-4">
                  <input
                    type="email"
                    name="email"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="flex-1 px-4 py-3 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white"
                    required
                  />
                  <button
                    type="submit"
                    className="px-6 py-3 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Subscribe
                  </button>
                </div>
                {newsletterSubmitted && (
                  <p className="text-green-200 text-center mt-4">
                    ✓ Successfully subscribed! Welcome to our newsletter.
                  </p>
                )}
              </form>
            </div>
          </div>
        </section>

        {/* Marketing Features */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                What You'll Receive
              </h2>
              <p className="text-xl text-gray-600">
                Stay ahead with our comprehensive marketing communications
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-mail-line text-2xl text-blue-600"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Weekly Newsletter</h3>
                <p className="text-gray-600 mb-4">
                  Comprehensive updates on new guidelines, regulatory changes, and industry trends delivered every Tuesday.
                </p>
                <ul className="text-sm text-gray-500 space-y-2">
                  <li>• New guideline releases</li>
                  <li>• Regulatory updates</li>
                  <li>• Industry best practices</li>
                  <li>• Upcoming webinar announcements</li>
                </ul>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-notification-line text-2xl text-green-600"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Urgent Alerts</h3>
                <p className="text-gray-600 mb-4">
                  Immediate notifications for critical guideline changes and time-sensitive updates that affect your business.
                </p>
                <ul className="text-sm text-gray-500 space-y-2">
                  <li>• Emergency guideline changes</li>
                  <li>• Compliance deadlines</li>
                  <li>• System maintenance alerts</li>
                  <li>• Critical industry news</li>
                </ul>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-gift-line text-2xl text-purple-600"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Exclusive Content</h3>
                <p className="text-gray-600 mb-4">
                  Premium resources, early access to new features, and special offers available only to subscribers.
                </p>
                <ul className="text-sm text-gray-500 space-y-2">
                  <li>• Premium guideline summaries</li>
                  <li>• Early feature access</li>
                  <li>• Exclusive webinars</li>
                  <li>• Special pricing offers</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Contact for Marketing */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Custom Marketing Solutions
              </h2>
              <p className="text-xl text-gray-600">
                Need personalized marketing support? Let's discuss how we can help promote your business.
              </p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8">
              <form onSubmit={handleContactSubmit} className="space-y-6" data-readdy-form>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company/Organization
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={contactForm.company}
                      onChange={(e) => setContactForm({...contactForm, company: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Marketing Type
                  </label>
                  <select
                    name="marketingType"
                    value={contactForm.marketingType}
                    onChange={(e) => setContactForm({...contactForm, marketingType: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                  >
                    <option value="email">Email Marketing</option>
                    <option value="sms">SMS/Text Marketing</option>
                    <option value="both">Both Email & SMS</option>
                    <option value="custom">Custom Solution</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tell us about your marketing needs
                  </label>
                  <textarea
                    name="message"
                    value={contactForm.message}
                    onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="Describe your target audience, marketing goals, and any specific requirements..."
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    {contactForm.message.length}/500 characters
                  </p>
                </div>

                <div className="text-center">
                  <button
                    type="submit"
                    className="px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Submit Request
                  </button>
                  {contactSubmitted && (
                    <p className="text-green-600 mt-4">
                      ✓ Request submitted successfully! We'll contact you within 24 hours.
                    </p>
                  )}
                </div>
              </form>
            </div>
          </div>
        </section>

        {/* Marketing Stats */}
        <section className="py-16 bg-blue-600">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">
                Join Our Growing Community
              </h2>
              <p className="text-xl text-blue-100">
                Thousands of mortgage professionals trust our marketing communications
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-4xl font-bold text-white mb-2">15,000+</div>
                <div className="text-blue-100">Newsletter Subscribers</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-white mb-2">98%</div>
                <div className="text-blue-100">Open Rate</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-white mb-2">24hrs</div>
                <div className="text-blue-100">Response Time</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-white mb-2">500+</div>
                <div className="text-blue-100">Companies Served</div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default MarketingPage;
